import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, FileText, CalendarCheck, Route, Upload, Download, Loader2, Video, Trash2, ClipboardList, CheckCircle2, PawPrint } from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "@/context/ClientAuthContext";
import clientApi from "@/lib/clientApi";
import api, { formatApiError } from "@/lib/api";
import PortalProgress from "@/components/PortalProgress";
import { ReferAndEarn } from "@/components/ReferAndEarn";
import { PushReminders } from "@/components/PushReminders";
import { FamilyPetsFields } from "@/components/FamilyPetsFields";
import { VideoCarousel } from "@/components/VideoCarousel";
import { PetStoryShare } from "@/components/PetStoryShare";
import TripLiveView from "@/components/TripLiveView";
import ClientTripPanel from "@/components/trip/ClientTripPanel";
import MyTripPhotos from "@/components/MyTripPhotos";
import { Navigation } from "lucide-react";
import Seo from "@/components/Seo";

const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const mxn = (n, rate) => rate ? new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format((n || 0) * rate) : "";
const STATUS_STYLE = { accepted: "bg-sage/15 text-sage", sent: "bg-terracotta-light text-terracotta-700", submitted: "bg-sand-200 text-charcoal-600", draft: "bg-sand-100 text-charcoal-500" };

export default function ClientPortal() {
  const { client, logout, updateClient } = useClientAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [trips, setTrips] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docReqs, setDocReqs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [reqTarget, setReqTarget] = useState(null);
  const fileRef = useRef(null);
  const reqFileRef = useRef(null);

  useEffect(() => {
    if (client === null) navigate("/account");
  }, [client, navigate]);

  const loadDocs = () => clientApi.get("/client/documents").then((r) => setDocs(r.data));
  const loadReqs = () => clientApi.get("/client/document-requests").then((r) => setDocReqs(r.data));

  useEffect(() => {
    if (!client) return;
    clientApi.get("/client/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    clientApi.get("/client/bookings").then((r) => setBookings(r.data)).catch(() => {});
    clientApi.get("/trips/mine").then((r) => setTrips(r.data)).catch(() => {});
    loadDocs().catch(() => {});
    loadReqs().catch(() => {});
  }, [client]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await clientApi.post("/client/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded.");
      loadDocs();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const download = async (doc) => {
    try {
      const res = await clientApi.get(`/client/documents/${doc.id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open the document."); }
  };

  const removeDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_filename}"? This can't be undone.`)) return;
    try {
      await clientApi.delete(`/client/documents/${doc.id}`);
      toast.success("Document deleted.");
      loadDocs();
      loadReqs().catch(() => {});
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const pickForRequest = (reqId) => { setReqTarget(reqId); reqFileRef.current?.click(); };

  const uploadForRequest = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !reqTarget) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await clientApi.post(`/client/document-requests/${reqTarget}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document received — thank you!");
      loadReqs();
      loadDocs();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
    setUploading(false);
    setReqTarget(null);
    if (reqFileRef.current) reqFileRef.current.value = "";
  };

  const handleLogout = () => { logout(); navigate("/"); };

  if (!client) return <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center"><Loader2 className="animate-spin text-terracotta" /></div>;

  return (
    <div className="min-h-screen bg-[#F7F4EF]">
      <Seo title="My account | Hola Movers" description="Your relocation quotes, consultations, and documents." noindex />
      <header className="bg-white border-b border-sand-200">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-5 h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/brand/logo-mark.png" alt="Hola Movers" className="h-8 w-auto" />
            <span className="font-serif text-lg text-charcoal-800">Hola Movers</span>
          </Link>
          <button data-testid="client-logout" onClick={handleLogout} className="inline-flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-terracotta">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8 space-y-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl text-charcoal-800" data-testid="portal-greeting">
              Hi {(client.preferred_name || client.name)?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="text-charcoal-600 mt-1" data-testid="portal-greeting-sub">
              {client.pets?.length
                ? `Here's everything for your move — we're excited to help ${client.pets.map((p) => p.name).filter(Boolean).join(" & ") || "your pets"} get comfortable in Mérida! 🐾`
                : "Here's everything for your move in one place."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <PushReminders email={client.email} />
            <label htmlFor="portal-lang" className="text-sm text-charcoal-500">Email me in</label>
            <select
              id="portal-lang"
              data-testid="portal-language-select"
              className="rounded-lg border border-sand-200 bg-white px-3 py-2 text-sm text-charcoal-700 focus:outline-none focus:ring-2 focus:ring-sage/50"
              value={client.preferred_language || "en"}
              onChange={async (e) => {
                const lang = e.target.value;
                try {
                  await clientApi.patch("/client/language", { preferred_language: lang });
                  updateClient({ preferred_language: lang });
                  toast.success(lang === "es" ? "Te enviaremos los correos en español." : "We'll email you in English.");
                } catch { toast.error("Could not save your language preference."); }
              }}
            >
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>
        </div>

        <PortalProgress quotes={quotes} bookings={bookings} docs={docs} />

        <ReferAndEarn />

        <FamilyPetsSection client={client} updateClient={updateClient} />

        <ChecklistProgressCard />

        <PetStoryShare client={client} />

        <VideoCarousel compact heading="Explore life in Mexico" />

        {/* Quotes */}
        <section data-testid="portal-quotes">
          <h2 className="flex items-center gap-2 font-serif text-xl text-charcoal-800 mb-4"><Route size={18} className="text-terracotta" /> Your quotes</h2>
          {quotes.length === 0 ? (
            <EmptyCard>No quotes yet. Once we prepare a relocation quote for you, it will appear here in USD and MXN.</EmptyCard>
          ) : (
            <div className="space-y-3">
              {quotes.map((q) => {
                const rate = q.pricing?.exchange_rate;
                return (
                  <div key={q.id} data-testid={`portal-quote-${q.id}`} className="rounded-xl border border-sand-200 bg-white p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-medium text-charcoal-800">{q.trip?.start} → {q.trip?.end}</div>
                        <div className="text-xs text-charcoal-500">{q.route?.distance_km} km · {q.route?.nights} overnight(s)</div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs capitalize ${STATUS_STYLE[q.status] || "bg-sand-100 text-charcoal-500"}`}>{q.deposit_paid ? "deposit paid" : q.status}</span>
                        <div className="font-serif text-terracotta mt-1">{usd(q.pricing?.total)}</div>
                        {rate && <div className="text-xs text-charcoal-400">{mxn(q.pricing?.total, rate)}</div>}
                      </div>
                    </div>
                    {q.accept_token && !q.deposit_paid && (
                      <Link to={`/quote/${q.accept_token}`} data-testid={`portal-quote-view-${q.id}`}
                        className="mt-3 inline-block rounded-full bg-terracotta px-4 py-2 text-sm text-white font-medium hover:bg-terracotta-hover">
                        View &amp; accept quote
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {trips.filter((t) => t.status !== "cancelled").length > 0 && (
          <section data-testid="portal-trips">
            <h2 className="flex items-center gap-2 font-serif text-xl text-charcoal-800 mb-4"><Navigation size={18} className="text-terracotta" /> Your pet's live journey</h2>
            <div className="space-y-6">
              {trips.filter((t) => t.status !== "cancelled").map((t) => (
                <div key={t.id} className="rounded-2xl border border-sand-200 bg-white p-4" data-testid={`portal-trip-${t.id}`}>
                  <div className="font-medium text-charcoal-800 mb-3">{t.name || `${t.origin?.name || "Start"} → ${t.destination?.name || "Mérida"}`}</div>
                  <TripLiveView load={() => clientApi.get(`/trips/${t.id}/live`).then((r) => r.data)} mapHeight={360} />
                  <ClientTripPanel tripId={t.id} status={t.status} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Consultations */}
        <section data-testid="portal-bookings">
          <h2 className="flex items-center gap-2 font-serif text-xl text-charcoal-800 mb-4"><CalendarCheck size={18} className="text-terracotta" /> Your consultations</h2>
          <div className="space-y-3">
            {bookings.map((b) => (
              <div key={b.id} className="rounded-xl border border-sand-200 bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-medium text-charcoal-800">{b.type_name} · {b.slot?.replace("T", " ")}</div>
                  <div className="text-xs text-charcoal-500 capitalize">{b.status}</div>
                </div>
                <div className="flex gap-2">
                  {b.meeting_link && <a href={`/call?token=${b.manage_token}`} className="inline-flex items-center gap-1 rounded-lg border border-sand-200 px-3 py-2 text-sm text-charcoal-700 hover:bg-sand-50"><Video size={14} /> Join</a>}
                  <Link to={`/booking/manage?token=${b.manage_token}`} className="inline-flex items-center gap-1 rounded-lg border border-sand-200 px-3 py-2 text-sm text-charcoal-700 hover:bg-sand-50">Reschedule</Link>
                </div>
              </div>
            ))}
            <Link to="/book" data-testid="portal-book-new" className="inline-block rounded-full bg-charcoal-800 px-5 py-2.5 text-sm text-white font-medium hover:bg-charcoal-900">Book a consultation</Link>
          </div>
        </section>

        {/* Requested documents checklist */}
        {docReqs.length > 0 && (
          <section data-testid="portal-doc-requests">
            <h2 className="flex items-center gap-2 font-serif text-xl text-charcoal-800 mb-4">
              <ClipboardList size={18} className="text-terracotta" /> Documents we need from you
              <span className="ml-1 text-sm font-sans text-charcoal-500" data-testid="doc-requests-progress">
                {docReqs.filter((r) => r.status !== "pending").length} of {docReqs.length} done
              </span>
            </h2>
            <div className="rounded-xl border border-sand-200 bg-white p-4">
              <input ref={reqFileRef} data-testid="req-file-input" type="file" onChange={uploadForRequest} className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.xls,.xlsx,.txt,.csv" />
              <div className="divide-y divide-sand-100">
                {docReqs.map((r) => (
                  <div key={r.id} data-testid={`doc-request-${r.id}`} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm font-medium text-charcoal-800">
                        {r.status === "received" ? <CheckCircle2 size={15} className="text-sage shrink-0" /> : <FileText size={15} className="text-charcoal-400 shrink-0" />}
                        {r.label}
                      </div>
                      {r.note && <p className="text-xs text-charcoal-500 mt-0.5 ml-6">{r.note}</p>}
                    </div>
                    {r.status === "pending" ? (
                      <button onClick={() => pickForRequest(r.id)} disabled={uploading} data-testid={`req-upload-${r.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-4 py-1.5 text-xs text-white font-medium hover:bg-terracotta-hover disabled:opacity-60 shrink-0">
                        {uploading && reqTarget === r.id ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
                      </button>
                    ) : (
                      <span className={`rounded-full px-3 py-1 text-xs font-medium shrink-0 ${r.status === "received" ? "bg-sage/15 text-sage" : "bg-sand-200 text-charcoal-500"}`}>
                        {r.status === "received" ? "Received" : "Not needed"}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Documents */}
        <section data-testid="portal-documents">
          <h2 className="flex items-center gap-2 font-serif text-xl text-charcoal-800 mb-4"><FileText size={18} className="text-terracotta" /> Your documents</h2>
          <div className="rounded-xl border border-sand-200 bg-white p-4">
            <input ref={fileRef} data-testid="doc-file-input" type="file" onChange={upload} className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx" />
            <button data-testid="doc-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm text-white font-medium hover:bg-terracotta-hover disabled:opacity-60">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload a document
            </button>
            <p className="text-xs text-charcoal-400 mt-2">Passports, residency paperwork, pet records — PDF or image, up to 15 MB.</p>
            <div className="mt-4 divide-y divide-sand-100">
              {docs.length === 0 && <p className="text-sm text-charcoal-500">No documents uploaded yet.</p>}
              {docs.map((d) => (
                <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-2 text-sm text-charcoal-700"><FileText size={15} className="text-charcoal-400" /> {d.original_filename}</div>
                  <div className="flex items-center gap-4">
                    <button onClick={() => download(d)} data-testid={`doc-open-${d.id}`} className="inline-flex items-center gap-1 text-sm text-terracotta hover:underline"><Download size={14} /> Open</button>
                    <button onClick={() => removeDoc(d)} data-testid={`doc-delete-${d.id}`} className="inline-flex items-center gap-1 text-sm text-charcoal-400 hover:text-red-600 transition-colors" aria-label={`Delete ${d.original_filename}`}><Trash2 size={14} /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* My Trip Photos */}
        <MyTripPhotos />
      </main>
    </div>
  );
}

function ChecklistProgressCard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/moving-checklist"), clientApi.get("/client/checklist-progress")])
      .then(([list, prog]) => {
        const total = list.data.items.length;
        const ids = new Set(list.data.items.map((i) => i.id));
        const done = (prog.data.checked || []).filter((id) => ids.has(id)).length;
        setStats({ total, done });
      }).catch(() => setStats(null));
  }, []);
  if (!stats || stats.total === 0) return null;
  const pct = Math.round((stats.done / stats.total) * 100);
  return (
    <Link to="/moving-checklist" data-testid="portal-checklist-card"
      className="block rounded-xl border border-sand-200 bg-white p-5 transition-colors hover:border-terracotta">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 font-medium text-charcoal-800"><ClipboardList size={16} className="text-terracotta" /> Moving checklist</span>
        <span className="font-serif text-terracotta">{stats.done}/{stats.total} · {pct}%</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-sand-200">
        <div className="h-full rounded-full bg-terracotta transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-xs text-charcoal-500">{pct === 100 ? "All done — you're Mérida-ready! 🎉" : "Tap to keep checking things off."}</p>
    </Link>
  );
}

function EmptyCard({ children }) {
  return <div className="rounded-xl border border-sand-200 bg-white p-5 text-sm text-charcoal-500">{children}</div>;
}

function FamilyPetsSection({ client, updateClient }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const startEdit = () => {
    setForm({
      preferred_name: client.preferred_name || "",
      party_adults: client.party_adults ?? null,
      party_kids: client.party_kids ?? null,
      personalization_notes: client.personalization_notes || "",
      pets: client.pets || [],
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await clientApi.patch("/client/profile", form);
      updateClient(data);
      toast.success("Saved — we'll keep this in mind for your move.");
      setEditing(false);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    setSaving(false);
  };

  const pets = client.pets || [];
  return (
    <section data-testid="portal-family-pets">
      <h2 className="flex items-center gap-2 font-serif text-xl text-charcoal-800 mb-4">
        <PawPrint size={18} className="text-terracotta" /> My family &amp; pets
      </h2>
      <div className="rounded-xl border border-sand-200 bg-white p-5">
        {editing ? (
          <div className="space-y-4">
            <FamilyPetsFields value={form} onChange={setForm} />
            <div className="flex gap-2">
              <button data-testid="profile-save-btn" onClick={save} disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover disabled:opacity-60">
                {saving && <Loader2 size={14} className="animate-spin" />} Save
              </button>
              <button onClick={() => setEditing(false)} className="rounded-full border border-sand-300 px-5 py-2 text-sm text-charcoal-700 hover:bg-sand-50">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2 text-sm text-charcoal-700">
              <p><span className="text-charcoal-400">Preferred name:</span> {client.preferred_name || client.name || "—"}</p>
              <p><span className="text-charcoal-400">Traveling:</span> {client.party_adults != null || client.party_kids != null
                ? `${client.party_adults ?? 0} adult(s), ${client.party_kids ?? 0} kid(s)` : "—"}</p>
              {client.personalization_notes && <p><span className="text-charcoal-400">Notes:</span> {client.personalization_notes}</p>}
              <div className="flex flex-wrap gap-2 pt-1" data-testid="portal-pet-chips">
                {pets.length === 0 && <span className="text-charcoal-400">No pets on file yet.</span>}
                {pets.map((p, i) => (
                  <span key={p.id || i} className="inline-flex items-center gap-1.5 rounded-full bg-sand-100 border border-sand-200 px-3 py-1 text-xs font-medium text-charcoal-700">
                    {p.photo ? <img src={p.photo} alt="" className="h-5 w-5 rounded-full object-cover" /> : <PawPrint size={11} className="text-terracotta" />}
                    {p.name || "Pet"}{p.species ? ` · ${p.species}` : ""}
                  </span>
                ))}
              </div>
            </div>
            <button data-testid="profile-edit-btn" onClick={startEdit}
              className="rounded-full border border-sand-300 px-5 py-2 text-sm font-medium text-charcoal-700 hover:border-terracotta hover:text-terracotta">
              {pets.length || client.preferred_name ? "Edit" : "Add details"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
